// Runtime state on top of the static presentation (7.6F): the current element
// state (StatePatch — CREATIVE-MODEL §5.2, e.g. a found difference or a matched
// card) and a runtime position (relocation, shuffled card slots, a snapped or
// dragged piece). Pure mapping of TYPED values, like style.ts; the animation
// driver keeps writing `transform`/`opacity`, so a state's own transform and
// opacity use the independent `translate`/`scale`/`rotate` and `filter`
// properties and both compose. Shared by the Studio and the served runtime (7.6H).

import type { StatePatch } from "@interactads/creative-model";
import type { Css, ElementPresentation } from "./style.js";

export interface RuntimeLook {
  readonly presentation: ElementPresentation;
  /** Extra declarations of the state (individual transforms, opacity filter, transition). */
  readonly css: Css;
  /** Image asset key swapped by the state. */
  readonly asset?: string;
  /** The state hides the layer. */
  readonly hidden: boolean;
}

export function runtimeLook(
  p: ElementPresentation,
  state: string | undefined,
  position: { readonly x: number; readonly y: number } | undefined
): RuntimeLook {
  const states = (p as { states?: Readonly<Record<string, StatePatch>> }).states;
  const patch = state !== undefined ? states?.[state] : undefined;
  const frame = p.frame as unknown as Record<string, unknown>;
  let next = position ? ({ ...p, frame: { ...frame, x: position.x, y: position.y } } as ElementPresentation) : p;
  const css: Css = {};
  if (!patch) return { presentation: next, css, hidden: false };
  const style = { ...((next.style as Record<string, unknown> | undefined) ?? {}) };
  if (patch.fill) style.fill = patch.fill;
  if (patch.border) style.border = patch.border;
  if (patch.shadows) style.shadows = patch.shadows;
  next = { ...next, style, ...(patch.text ? { text: patch.text } : {}) } as ElementPresentation;
  if (patch.opacity !== undefined) css.filter = `opacity(${String(patch.opacity)})`;
  if (patch.scale !== undefined) css.scale = String(patch.scale);
  if (patch.rotation !== undefined) css.rotate = `${String(patch.rotation)}deg`;
  if (patch.offset) css.translate = `${String(patch.offset.x)}px ${String(patch.offset.y)}px`;
  if (patch.transition) {
    const t = `${String(patch.transition.duration)}ms`;
    css.transition = ["filter", "scale", "rotate", "translate", "background", "border-color", "box-shadow"]
      .map((x) => `${x} ${t}`)
      .join(", ");
  }
  return {
    presentation: next,
    css,
    ...(patch.asset !== undefined ? { asset: patch.asset } : {}),
    hidden: patch.visible === false,
  };
}
